import React, { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    // En producción, podrías enviar esto a un servicio de logging
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-[#0a0a0a] text-white px-4">
          <AlertTriangle className="w-16 h-16 text-primary mb-4 animate-pulse" />
          <h1 className="text-2xl font-bold mb-2">Algo salió mal</h1>
          <p className="text-gray-400 text-center max-w-md mb-6">
            La aplicación encontró un error inesperado. Intenta recargar la página.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-[#DC394B] hover:bg-[#f34f60] rounded-full font-semibold transition-colors"
          >
            Recargar página
          </button>
          {process.env.NODE_ENV === "development" && this.state.error && (
            <pre className="mt-6 p-4 bg-[#1a1a1a] rounded-lg text-xs text-red-400 max-w-full overflow-auto">
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
